import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Chip,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Autocomplete,
  Slider,
  Divider,
  Tab,
  Tabs,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationCity as CityIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Timeline as TimelineIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  AccountBalance as FederalIcon,
  Security as DefenseIcon,
  Memory as TechIcon,
  Public as RegionIcon,
  Map as MapIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { rev108Service, GenerationJob, REV108Lead, REV108Stats, StateInfo, RegionInfo, LocationStats, CityWithState, IndustryCategory } from '../services/rev108Service';

const REV108Page: React.FC = () => {
  const theme = useTheme();
  
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [leads, setLeads] = useState<REV108Lead[]>([]);
  const [jobs, setJobs] = useState<GenerationJob[]>([]);
  const [stats, setStats] = useState<REV108Stats | null>(null);
  const [industries, setIndustries] = useState<string[]>([]);
  const [industriesGrouped, setIndustriesGrouped] = useState<IndustryCategory[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [allStates, setAllStates] = useState<StateInfo[]>([]);
  const [allRegions, setAllRegions] = useState<RegionInfo[]>([]);
  const [locationStats, setLocationStats] = useState<LocationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalLeads, setTotalLeads] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  
  // Filters
  const [searchText, setSearchText] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState<boolean | undefined>(undefined);
  
  // Generation dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState('General Contractors');
  const [targetCount, setTargetCount] = useState(50);
  const [emailRate, setEmailRate] = useState(0.4);
  
  // Location selection mode
  const [locationMode, setLocationMode] = useState<'cities' | 'state' | 'region' | 'hub'>('cities');
  const [selectedStateAbbrev, setSelectedStateAbbrev] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [hubFilter, setHubFilter] = useState<'federal' | 'defense' | 'tech' | 'top' | ''>('');
  
  // Active job tracking
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [activeJob, setActiveJob] = useState<GenerationJob | null>(null);
  
  // Lead detail dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<REV108Lead | null>(null);
  
  // Notification
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Poll active job status
  useEffect(() => {
    if (activeJobId) {
      const interval = setInterval(async () => {
        try {
          const job = await rev108Service.getJobStatus(activeJobId);
          setActiveJob(job);
          
          if (job.status === 'completed' || job.status === 'failed') {
            clearInterval(interval);
            setActiveJobId(null);
            fetchLeads();
            fetchStats();
            fetchJobs();
            
            if (job.status === 'completed') {
              setSnackbar({ open: true, message: `Generation complete! ${job.leadsGenerated} leads generated with ${job.emailsFound} emails.`, severity: 'success' });
            } else {
              setSnackbar({ open: true, message: `Generation failed: ${job.error}`, severity: 'error' });
            }
          }
        } catch (error) {
          console.error('Failed to fetch job status:', error);
        }
      }, 3000);
      
      return () => clearInterval(interval);
    }
  }, [activeJobId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchIndustries(),
        fetchCities(),
        fetchLeads(),
        fetchStats(),
        fetchJobs(),
        fetchStates(),
        fetchRegions(),
        fetchLocationStats(),
      ]);
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchIndustries = async () => {
    try {
      const data = await rev108Service.getIndustries();
      setIndustries(data);
      // Also fetch grouped industries
      const grouped = await rev108Service.getIndustriesGrouped();
      setIndustriesGrouped(grouped);
    } catch (error) {
      console.error('Failed to fetch industries:', error);
    }
  };

  const fetchCities = async () => {
    try {
      const data = await rev108Service.getCities();
      setAvailableCities(data as string[]);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  const fetchStates = async () => {
    try {
      const data = await rev108Service.getStates();
      setAllStates(data);
    } catch (error) {
      console.error('Failed to fetch states:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const data = await rev108Service.getRegions();
      setAllRegions(data);
    } catch (error) {
      console.error('Failed to fetch regions:', error);
    }
  };

  const fetchLocationStats = async () => {
    try {
      const data = await rev108Service.getLocationStats();
      setLocationStats(data);
    } catch (error) {
      console.error('Failed to fetch location stats:', error);
    }
  };

  const fetchLeads = useCallback(async () => {
    try {
      const response = await rev108Service.getLeads({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: searchText || undefined,
        city: cityFilter || undefined,
        hasEmail: emailFilter,
      });
      setLeads(response.leads);
      setTotalLeads(response.total);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  }, [paginationModel, searchText, cityFilter, emailFilter]);

  const fetchStats = async () => {
    try {
      const data = await rev108Service.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const data = await rev108Service.getJobs();
      setJobs(data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchLeads();
    }
  }, [fetchLeads, loading]);

  const handleStartGeneration = async () => {
    if (selectedCities.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one city', severity: 'error' });
      return;
    }
    
    setGenerating(true);
    try {
      const result = await rev108Service.startGeneration({
        cities: selectedCities,
        industry: selectedIndustry,
        targetCount,
        emailRate,
      });
      
      setActiveJobId(result.jobId);
      setGenerateDialogOpen(false);
      setSnackbar({ open: true, message: 'Lead generation started!', severity: 'info' });
      fetchJobs();
    } catch (error) {
      console.error('Failed to start generation:', error);
      setSnackbar({ open: true, message: 'Failed to start generation', severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await rev108Service.exportLeads(
        selectedRows.length > 0 ? selectedRows.map(String) : undefined,
        { city: cityFilter, hasEmail: emailFilter }
      );
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rev108_leads_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSnackbar({ open: true, message: 'Leads exported successfully', severity: 'success' });
    } catch (error) {
      console.error('Failed to export leads:', error);
      setSnackbar({ open: true, message: 'Failed to export leads', severity: 'error' });
    }
  };

  const handleDeleteLead = async (id: string) => {
    try {
      await rev108Service.deleteLead(id);
      setSnackbar({ open: true, message: 'Lead deleted', severity: 'success' });
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete lead:', error);
      setSnackbar({ open: true, message: 'Failed to delete lead', severity: 'error' });
    }
  };

  const handleViewLead = (lead: REV108Lead) => {
    setSelectedLead(lead);
    setViewDialogOpen(true);
  };

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'company',
      headerName: 'Company',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Contact',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ color: 'success.main', fontSize: 16 }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled">—</Typography>
        )
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ color: 'info.main', fontSize: 16 }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled">—</Typography>
        )
      ),
    },
    {
      field: 'city',
      headerName: 'City',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 'N/A'}
          size="small"
          variant="outlined"
          icon={<CityIcon sx={{ fontSize: 14 }} />}
        />
      ),
    },
    {
      field: 'parsedNotes',
      headerName: 'Score',
      width: 80,
      renderCell: (params: GridRenderCellParams) => {
        const score = params.value?.leadScore || 0;
        const color = score >= 70 ? 'success' : score >= 40 ? 'warning' : 'error';
        return (
          <Chip
            label={score}
            size="small"
            color={color}
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewLead(params.row)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDeleteLead(params.row.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            REV108 Lead Generation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI-powered lead generation with email discovery and Google verification
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={leads.length === 0}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setGenerateDialogOpen(true)}
            disabled={!!activeJobId}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}
          >
            Generate Leads
          </Button>
        </Box>
      </Box>

      {/* Active Job Progress */}
      {activeJob && activeJob.status === 'running' && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.info.main, 0.05), border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="subtitle1" fontWeight={600}>
              Generating Leads...
            </Typography>
            <Chip label={`${activeJob.progress}%`} size="small" color="info" />
          </Box>
          <LinearProgress variant="determinate" value={activeJob.progress} sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {activeJob.cities.join(', ')} • {activeJob.industry} • 
            Target: {activeJob.targetCount} • Generated: {activeJob.leadsGenerated} • Emails: {activeJob.emailsFound}
          </Typography>
        </Paper>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">Total Leads</Typography>
                  <Typography variant="h4" fontWeight={700}>{stats?.totalLeads || 0}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                  <BusinessIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">With Email</Typography>
                  <Typography variant="h4" fontWeight={700}>{stats?.leadsWithEmail || 0}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                  <EmailIcon sx={{ color: 'success.main', fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">Email Rate</Typography>
                  <Typography variant="h4" fontWeight={700}>{stats?.emailRate || 0}%</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                  <TrendingUpIcon sx={{ color: 'info.main', fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">Recent (7d)</Typography>
                  <Typography variant="h4" fontWeight={700}>{stats?.recentLeads || 0}</Typography>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                  <TimelineIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="Generated Leads" />
        <Tab label={
          <Badge badgeContent={jobs.filter(j => j.status === 'running').length} color="primary">
            Job History
          </Badge>
        } />
      </Tabs>

      {/* Leads Tab */}
      {activeTab === 0 && (
        <Paper sx={{ p: 2 }}>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search leads..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>City</InputLabel>
              <Select
                value={cityFilter}
                label="City"
                onChange={(e) => setCityFilter(e.target.value)}
              >
                <MenuItem value="">All Cities</MenuItem>
                {stats?.leadsByCity?.map((c) => (
                  <MenuItem key={c.city} value={c.city}>
                    {c.city} ({c.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Email</InputLabel>
              <Select
                value={emailFilter === undefined ? '' : emailFilter.toString()}
                label="Email"
                onChange={(e) => setEmailFilter(e.target.value === '' ? undefined : e.target.value === 'true')}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Has Email</MenuItem>
                <MenuItem value="false">No Email</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => { fetchLeads(); fetchStats(); }}
            >
              Refresh
            </Button>
          </Box>

          {/* Data Grid */}
          <DataGrid
            rows={leads}
            columns={columns}
            rowCount={totalLeads}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            checkboxSelection
            onRowSelectionModelChange={setSelectedRows}
            rowSelectionModel={selectedRows}
            loading={loading}
            autoHeight
            disableRowSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderColor: alpha(theme.palette.divider, 0.05),
              },
            }}
          />
        </Paper>
      )}

      {/* Jobs Tab */}
      {activeTab === 1 && (
        <Paper sx={{ p: 2 }}>
          {jobs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No generation jobs yet</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {jobs.map((job) => (
                <Paper key={job.id} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {job.industry}
                        </Typography>
                        <Chip
                          label={job.status}
                          size="small"
                          color={
                            job.status === 'completed' ? 'success' :
                            job.status === 'running' ? 'info' :
                            job.status === 'failed' ? 'error' : 'default'
                          }
                          icon={
                            job.status === 'completed' ? <CheckCircleIcon /> :
                            job.status === 'running' ? <PlayIcon /> :
                            job.status === 'failed' ? <ErrorIcon /> : undefined
                          }
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Cities: {job.cities.join(', ')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Target: {job.targetCount} • Generated: {job.leadsGenerated} • Emails: {job.emailsFound}
                      </Typography>
                      {job.error && (
                        <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                          Error: {job.error}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(job.startedAt).toLocaleString()}
                    </Typography>
                  </Box>
                  {job.status === 'running' && (
                    <LinearProgress variant="determinate" value={job.progress} sx={{ mt: 2 }} />
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* Generate Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SpeedIcon sx={{ color: 'primary.main' }} />
            Generate New Leads
            {locationStats && (
              <Chip 
                size="small" 
                label={`${locationStats.totalCities} cities • ${locationStats.totalStates} states`} 
                sx={{ ml: 'auto' }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            
            {/* Location Selection Mode */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MapIcon fontSize="small" /> Location Selection Mode
              </Typography>
              <ToggleButtonGroup
                value={locationMode}
                exclusive
                onChange={(_, value) => {
                  if (value) {
                    setLocationMode(value);
                    setSelectedCities([]);
                    setSelectedStateAbbrev('');
                    setSelectedRegion('');
                    setHubFilter('');
                  }
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value="cities">
                  <CityIcon sx={{ mr: 0.5 }} fontSize="small" /> Cities
                </ToggleButton>
                <ToggleButton value="state">
                  <MapIcon sx={{ mr: 0.5 }} fontSize="small" /> By State
                </ToggleButton>
                <ToggleButton value="region">
                  <RegionIcon sx={{ mr: 0.5 }} fontSize="small" /> By Region
                </ToggleButton>
                <ToggleButton value="hub">
                  <FederalIcon sx={{ mr: 0.5 }} fontSize="small" /> Hubs
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Mode: Cities - Manual Selection */}
            {locationMode === 'cities' && (
              <Autocomplete
                multiple
                options={availableCities}
                value={selectedCities}
                onChange={(_, newValue) => setSelectedCities(newValue)}
                groupBy={(option) => {
                  // Simple region grouping based on known federal hubs
                  const ncrCities = ['Washington DC', 'Arlington', 'Alexandria', 'Fairfax', 'Reston', 'McLean', 'Tysons', 'Bethesda', 'Rockville', 'Silver Spring'];
                  const floridaCities = ['Tampa', 'Orlando', 'Miami', 'Jacksonville', 'St. Petersburg', 'Melbourne', 'Fort Lauderdale', 'Pensacola', 'Panama City'];
                  const texasCities = ['Houston', 'Dallas', 'Austin', 'San Antonio', 'Fort Worth', 'Plano', 'El Paso'];
                  const californiaCities = ['San Diego', 'Los Angeles', 'San Francisco', 'San Jose', 'Irvine', 'Sacramento'];
                  const southeastCities = ['Atlanta', 'Charlotte', 'Nashville', 'Birmingham', 'Huntsville', 'Raleigh', 'Durham', 'Charleston'];
                  const midAtlanticCities = ['Philadelphia', 'Baltimore', 'Richmond', 'Norfolk', 'Virginia Beach', 'Newport News'];
                  const midwestCities = ['Chicago', 'Detroit', 'Columbus', 'Indianapolis', 'Minneapolis', 'St. Louis', 'Cincinnati', 'Cleveland'];
                  const mountainCities = ['Denver', 'Phoenix', 'Salt Lake City', 'Albuquerque', 'Colorado Springs', 'Las Vegas'];
                  
                  if (ncrCities.includes(option)) return '🏛️ National Capital Region';
                  if (floridaCities.includes(option)) return '🌴 Florida';
                  if (texasCities.includes(option)) return '⭐ Texas';
                  if (californiaCities.includes(option)) return '🌊 California';
                  if (southeastCities.includes(option)) return '🍑 Southeast';
                  if (midAtlanticCities.includes(option)) return '🗽 Mid-Atlantic';
                  if (midwestCities.includes(option)) return '🌾 Midwest';
                  if (mountainCities.includes(option)) return '⛰️ Mountain/Southwest';
                  return '🌎 Other Locations';
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Cities"
                    placeholder="Select cities..."
                    helperText={`${selectedCities.length} cities selected`}
                  />
                )}
              />
            )}

            {/* Mode: State Selection */}
            {locationMode === 'state' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Select State</InputLabel>
                  <Select
                    value={selectedStateAbbrev}
                    label="Select State"
                    onChange={async (e) => {
                      const stateAbbr = e.target.value;
                      setSelectedStateAbbrev(stateAbbr);
                      // Fetch cities for this state
                      try {
                        const stateCities = await rev108Service.getCities(undefined, stateAbbr);
                        setSelectedCities(stateCities as string[]);
                      } catch (err) {
                        console.error('Failed to fetch state cities:', err);
                      }
                    }}
                  >
                    {allStates.map((state) => (
                      <MenuItem key={state.abbrev} value={state.abbrev}>
                        {state.name} ({state.abbrev}) - {state.region}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedStateAbbrev && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    <strong>{selectedCities.length}</strong> cities will be targeted in {allStates.find(s => s.abbrev === selectedStateAbbrev)?.name}
                  </Alert>
                )}
              </Box>
            )}

            {/* Mode: Region Selection */}
            {locationMode === 'region' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Select Region</InputLabel>
                  <Select
                    value={selectedRegion}
                    label="Select Region"
                    onChange={async (e) => {
                      const region = e.target.value;
                      setSelectedRegion(region);
                      // Fetch cities for this region
                      try {
                        const regionCities = await rev108Service.getCities(undefined, undefined, region);
                        setSelectedCities(regionCities as string[]);
                      } catch (err) {
                        console.error('Failed to fetch region cities:', err);
                      }
                    }}
                  >
                    {allRegions.map((region) => (
                      <MenuItem key={region.name} value={region.name}>
                        {region.name} ({region.cityCount} cities • {region.states.length} states)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedRegion && (
                  <Box>
                    <Alert severity="info" sx={{ py: 0.5, mb: 1 }}>
                      <strong>{selectedCities.length}</strong> cities across {allRegions.find(r => r.name === selectedRegion)?.states.length} states
                    </Alert>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {allRegions.find(r => r.name === selectedRegion)?.states.slice(0, 8).map(s => (
                        <Chip key={s.abbrev} label={s.abbrev} size="small" variant="outlined" />
                      ))}
                      {(allRegions.find(r => r.name === selectedRegion)?.states.length || 0) > 8 && (
                        <Chip label={`+${(allRegions.find(r => r.name === selectedRegion)?.states.length || 0) - 8} more`} size="small" />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Mode: Hub Type Selection */}
            {locationMode === 'hub' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Target cities with high concentration of federal contractors and opportunities
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<FederalIcon />}
                    label={`Federal Hubs (${locationStats?.federalHubs || 0})`}
                    color={hubFilter === 'federal' ? 'primary' : 'default'}
                    variant={hubFilter === 'federal' ? 'filled' : 'outlined'}
                    onClick={async () => {
                      setHubFilter('federal');
                      try {
                        const cities = await rev108Service.getCities('federal');
                        setSelectedCities(cities as string[]);
                      } catch (err) {
                        console.error('Failed to fetch federal hubs:', err);
                      }
                    }}
                  />
                  <Chip
                    icon={<DefenseIcon />}
                    label={`Defense Hubs (${locationStats?.defenseHubs || 0})`}
                    color={hubFilter === 'defense' ? 'secondary' : 'default'}
                    variant={hubFilter === 'defense' ? 'filled' : 'outlined'}
                    onClick={async () => {
                      setHubFilter('defense');
                      try {
                        const cities = await rev108Service.getCities('defense');
                        setSelectedCities(cities as string[]);
                      } catch (err) {
                        console.error('Failed to fetch defense hubs:', err);
                      }
                    }}
                  />
                  <Chip
                    icon={<TechIcon />}
                    label={`Tech Hubs (${locationStats?.techHubs || 0})`}
                    color={hubFilter === 'tech' ? 'info' : 'default'}
                    variant={hubFilter === 'tech' ? 'filled' : 'outlined'}
                    onClick={async () => {
                      setHubFilter('tech');
                      try {
                        const cities = await rev108Service.getCities('tech');
                        setSelectedCities(cities as string[]);
                      } catch (err) {
                        console.error('Failed to fetch tech hubs:', err);
                      }
                    }}
                  />
                  <Chip
                    icon={<TrendingUpIcon />}
                    label="Top Lead Cities (60)"
                    color={hubFilter === 'top' ? 'success' : 'default'}
                    variant={hubFilter === 'top' ? 'filled' : 'outlined'}
                    onClick={async () => {
                      setHubFilter('top');
                      try {
                        const cities = await rev108Service.getCities('top');
                        setSelectedCities(cities as string[]);
                      } catch (err) {
                        console.error('Failed to fetch top cities:', err);
                      }
                    }}
                  />
                </Stack>
                {hubFilter && (
                  <Alert severity="success" sx={{ py: 0.5 }}>
                    <strong>{selectedCities.length}</strong> {hubFilter} hub cities selected
                  </Alert>
                )}
              </Box>
            )}

            {/* Selected Cities Preview */}
            {selectedCities.length > 0 && locationMode !== 'cities' && (
              <Box>
                <Typography variant="caption" color="text.secondary">Selected Cities Preview:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, maxHeight: 80, overflow: 'auto' }}>
                  {selectedCities.slice(0, 15).map(city => (
                    <Chip key={city} label={city} size="small" variant="outlined" />
                  ))}
                  {selectedCities.length > 15 && (
                    <Chip label={`+${selectedCities.length - 15} more`} size="small" color="primary" />
                  )}
                </Box>
              </Box>
            )}

            <Divider />
            
            {/* Industry Selection - Now using grouped industries */}
            <FormControl fullWidth>
              <InputLabel>Industry</InputLabel>
              <Select
                value={selectedIndustry}
                label="Industry"
                onChange={(e) => setSelectedIndustry(e.target.value)}
              >
                {industriesGrouped.length > 0 ? (
                  industriesGrouped.map((category) => [
                    <MenuItem key={`cat-${category.category}`} disabled sx={{ fontWeight: 700, color: 'primary.main', opacity: 1 }}>
                      — {category.category} —
                    </MenuItem>,
                    ...category.industries.map((industry) => (
                      <MenuItem key={industry} value={industry} sx={{ pl: 4 }}>{industry}</MenuItem>
                    ))
                  ]).flat()
                ) : (
                  industries.map((industry) => (
                    <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <Box>
              <Typography gutterBottom>Target Lead Count: {targetCount}</Typography>
              <Slider
                value={targetCount}
                onChange={(_, value) => setTargetCount(value as number)}
                min={10}
                max={200}
                step={10}
                marks={[
                  { value: 10, label: '10' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                  { value: 200, label: '200' },
                ]}
              />
            </Box>

            <Box>
              <Typography gutterBottom>Email Discovery Rate: {Math.round(emailRate * 100)}%</Typography>
              <Slider
                value={emailRate}
                onChange={(_, value) => setEmailRate(value as number)}
                min={0.1}
                max={1}
                step={0.1}
                marks={[
                  { value: 0.2, label: '20%' },
                  { value: 0.5, label: '50%' },
                  { value: 0.8, label: '80%' },
                  { value: 1, label: '100%' },
                ]}
              />
              <Typography variant="caption" color="text.secondary">
                Higher rates increase processing time but find more emails
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleStartGeneration}
            disabled={generating || selectedCities.length === 0}
            startIcon={generating ? <CircularProgress size={16} /> : <PlayIcon />}
          >
            {generating ? 'Starting...' : 'Start Generation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Lead Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Lead Details</DialogTitle>
        <DialogContent>
          {selectedLead && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="h6">{selectedLead.company}</Typography>
              <Divider />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Contact Name</Typography>
                  <Typography>{selectedLead.name || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Title</Typography>
                  <Typography>{selectedLead.parsedNotes?.title || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography>{selectedLead.email || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography>{selectedLead.phone || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">City</Typography>
                  <Typography>{selectedLead.city}, {selectedLead.state}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Lead Score</Typography>
                  <Chip label={selectedLead.parsedNotes?.leadScore || 0} size="small" color="primary" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Website</Typography>
                  {selectedLead.parsedNotes?.website ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>{selectedLead.parsedNotes.website}</Typography>
                      <IconButton size="small" href={selectedLead.parsedNotes.website} target="_blank">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Typography>—</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Address</Typography>
                  <Typography>{selectedLead.parsedNotes?.address || '—'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography>{selectedLead.service_interest || '—'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default REV108Page;
